'use client'

import { createGlobalStyle } from 'styled-components'
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import {
  saveQuestionResponse,
  submitAssessment,
  resetResponses,
  tickQuestionTime,
  pauseQuestionTime,
  initQuestionTime,
} from '../../actions'
import selectors from '../../selectors'
import Completion from './completion'
import PluginLiveLogo from 'components/icons/PluginLiveLogo'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import studentRequest from 'utils/studentRequest'
import useProctoringCollector from 'utils/useProctoringCollector'
import useCameraGuard, { isCameraLive } from 'utils/useCameraGuard'
import { getUserMediaWithSelectedDevices } from 'utils/mediaDevices'
import { mountHiddenProctorVideo, unmountProctorVideo, watchProctorTrack } from 'utils/proctorVideo'
import { shouldEnforceFullscreen, markMobileInterruption, isMobileInterruptionFullscreenExit } from 'utils/deviceTier'
import {
  trackAssessmentAutoSubmitted,
  trackAssessmentCompleted,
  trackAssessmentDroppedOff,
  trackDiagnosisCompleted,
  trackDiagnosisProgressUpdated,
  trackFullscreenEntered,
  trackFullscreenExited,
  trackQuestionAnswered,
  trackQuestionSkipped,
  trackRightClick,
  trackViolationDetected,
  trackPracticeUnlocked,
  trackPracticeSessionCompleted,
  trackDiagnosisStarted,
  trackAssessmentStarted,
  trackPracticeSessionDroppedOff,
  trackDiagnosisDroppedOff,
} from '../../../../utils/assessmentEvents'

// Constants
const MAX_TAB_SWITCH_WARNINGS = 4

const MarkdownGlobalStyle = createGlobalStyle`
  .markdown-body, .markdown-body * {
    color: #374151 !important;
  }
`

// UPDATED: Enhanced Fullscreen Assessment Container that covers entire viewport
const FullscreenAssessmentContainer = styled.div`
  position: ${props => (props.isFullscreen ? 'fixed' : 'relative')};
  top: ${props => (props.isFullscreen ? '0' : 'auto')};
  left: ${props => (props.isFullscreen ? '0' : 'auto')};
  width: ${props => (props.isFullscreen ? '100vw' : '100%')};
  height: ${props => (props.isFullscreen ? '100vh' : 'auto')};
  min-height: ${props => (props.isFullscreen ? '100vh' : 'auto')};
  z-index: ${props => (props.isFullscreen ? '99999' : 'auto')};
  display: flex;
  background: #f9fafb;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
`

const SidebarNav = styled.div`
  width: ${props => (props.isExpanded ? '25%' : '60px')};
  min-width: ${props => (props.isExpanded ? '300px' : '60px')};
  max-width: ${props => (props.isExpanded ? '25%' : '60px')};
  height: 100vh;
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 100;
  overflow: hidden;
`

const SidebarHeader = styled.div`
  padding: ${props => (props.isExpanded ? '20px 16px' : '20px 0')};
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  min-height: 64px;
  flex-shrink: 0;

  /* When expanded, use space-between for title and button */
  ${props =>
    props.isExpanded &&
    `
    justify-content: space-between;
  `}
`

const SidebarTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: #374151;
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;

  /* Use display instead of visibility/opacity for better centering */
  display: ${props => (props.isExpanded ? 'block' : 'none')};
`

const ToggleButton = styled.button`
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${props => (props.isExpanded ? '16px 12px' : '16px 6px')};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }
`

const SectionTitle = styled.h4`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 12px 0;
  padding: 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  white-space: nowrap;
`

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const QuestionItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => (props.isExpanded ? '10px 8px' : '12px 6px')};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  min-height: ${props => (props.isExpanded ? '40px' : '48px')};
  position: relative;
  margin-bottom: 2px;

  &:hover {
    background: #f8fafc;
    border-color: #d1d5db;
    transform: translateX(2px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  ${props =>
    props.isExpanded
      ? ''
      : `
    justify-content: center;
  `}
`

const QuestionNumber = styled.div`
  width: ${props => (props.isExpanded ? '28px' : '36px')};
  height: ${props => (props.isExpanded ? '28px' : '36px')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${props => (props.isExpanded ? '12px' : '13px')};
  flex-shrink: 0;
  position: relative;
  transition: all 0.2s ease;

  ${props => {
    switch (props.status) {
      case 'current':
        return `
          background: #3b82f6;
          color: white;
          box-shadow: 0 0 0 3px #eff6ff;
          border: 2px solid #3b82f6;
        `
      case 'attempted':
        return `
          background: #10b981;
          color: white;
          border: 2px solid #10b981;
        `
      case 'skipped':
        return `
          background: #6b7280;
          color: white;
          border: 2px solid #6b7280;
        `
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
          border: 2px solid #e5e7eb;
        `
    }
  }}
`

const QuestionText = styled.div`
  margin-left: ${props => (props.isExpanded ? '8px' : '0')};
  font-size: 12px;
  color: #4b5563;
  font-weight: 500;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  flex: 1;
  line-height: 1.3;
  display: flex;
  align-items: center;
  ${props =>
    props.isExpanded &&
    `
    display: flex;
    align-items: center;
    overflow: hidden;
    white-space: normal;
    /* Clamp to 2 lines with ellipsis */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `}
  ${props =>
    !props.isExpanded &&
    `
    display: none;
  `}
  & .markdown-body > * {
    margin: 0 !important;
  }
`
const QuestionStatus = styled.div`
  margin-left: auto;
  font-size: 12px;
  opacity: ${props => (props.isExpanded ? 1 : 0)};
  visibility: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: all 0.3s ease;
  flex-shrink: 0;

  ${props => {
    switch (props.status) {
      case 'attempted':
        return 'color: #10b981;'
      case 'skipped':
        return 'color: #6b7280;'
      case 'current':
        return 'color: #3b82f6;'
      default:
        return 'color: #9ca3af;'
    }
  }}
`

// Simplified Modal Components
const FullscreenModalOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
`

const FullscreenModalContent = styled.div`
  max-width: 500px;
  width: 90%;
  padding: 30px;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  position: relative;
  margin: 20px;
  max-height: 90vh;
  overflow-y: auto;
`

const FullscreenTitle = styled.h2`
  color: #4318ff;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
`

const FullscreenDescription = styled.p`
  margin-bottom: 20px;
  line-height: 1.5;
  color: #374151;
  font-size: 14px;

  strong {
    font-weight: 600;
    color: #111827;
  }

  ul {
    text-align: left;
    margin: 10px 0;
    padding-left: 20px;

    li {
      margin: 8px 0;
      color: #4b5563;
    }
  }
`

const PrimaryButton = styled.button`
  background-color: #4318ff;
  color: white;
  font-weight: bold;
  height: 44px;
  border-radius: 4px;
  border: none;
  padding: 0 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 180px;

  &:hover {
    background-color: #3512cc;
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(67, 24, 255, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(67, 24, 255, 0.2);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

const InfoContainer = styled.div`
  background-color: #f9fafb;
  padding: 16px 20px;
  border-radius: 8px;
  margin-bottom: 30px;
  display: flex;
  align-items: flex-start;
`

const InfoIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background-color: #4318ff;
  color: white;
  border-radius: 50%;
  font-size: 14px;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
`

const InfoText = styled.p`
  font-weight: 400;
  font-style: italic;
  font-size: 15px;
  line-height: 109%;
  letter-spacing: 0%;
  color: #111827;
  margin: 0;

  strong {
    font-weight: 600;
    font-style: italic;
    line-height: 100%;
    letter-spacing: 0%;
  }
`

// ADDED: Missing SecondaryButton component
const SecondaryButton = styled.button`
  background-color: #ffffff;
  color: #374151;
  font-weight: 500;
  height: 44px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  padding: 0 24px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(209, 213, 219, 0.2);
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
    border-color: #e5e7eb;
  }
`

// Improved Content Area with smooth layout transitions
const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const AssessmentContent = styled.div`
  flex: 1;
  padding: 20px 24px 100px 24px; /* Added bottom padding for sticky buttons */
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`

const AssessmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px; /* Increased from 32px */

  svg {
    height: 56px; /* Increased from 28px */
    width: auto;
  }

  img {
    height: 56px; /* Increased from 28px */
    width: auto;
    object-fit: contain;
  }

  /* Target the PluginLiveLogo component specifically */
  > * {
    display: flex;
    align-items: center;
    height: 56px; /* Increased from 28px */
    width: auto;
  }

  @media (max-width: 768px) {
    height: 36px;
    svg,
    img {
      height: 32px; /* Increased from 24px */
      width: auto;
    }

    > * {
      height: 32px; /* Increased from 24px */
      width: auto;
    }
  }

  @media (max-width: 480px) {
    height: 32px;
    svg,
    img {
      height: 28px; /* Increased from 20px */
      width: auto;
    }

    > * {
      height: 28px; /* Increased from 20px */
      width: auto;
    }
  }
`

const AssessmentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px; /* Increased from 32px to match LogoContainer */

  @media (max-width: 480px) {
    gap: 8px;
    height: 32px;
  }
`

const VerticalSeparator = styled.div`
  width: 1px;
  height: 28px; /* Increased from 28px to match logo height */
  background: #d1d5db;
  margin: 0 4px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    height: 32px;
  }

  @media (max-width: 480px) {
    height: 28px;
    margin: 0 2px;
  }
`

const AssessmentTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  line-height: 1.2; /* Control line height for better alignment */
  display: flex;
  align-items: center;
`

const AssessmentMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
`

const AssessmentTimer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #4318ff;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 600;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const AssessmentSubtitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 20px 0;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const SubtitleText = styled.h2`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin: 0;
`

const NegativeMarkingInfo = styled.div`
  font-size: 13px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #fee2e2;
  display: flex;
  align-items: center;
  gap: 6px;
`

const InfoIconSvg = styled.svg`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  padding: 1px;
`

// Status section moved above question
const StatusSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const StatusItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-width: 60px;
`

const StatusNumber = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.color || '#3b82f6'};
  margin-bottom: 4px;
`

const StatusLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ProgressContainer = styled.div`
  flex: 1;
  margin: 0 20px;
`

const ProgressText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 6px;
  text-align: center;
`

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.progress}%;
    background: #3b82f6;
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`

// Simplified Question Container
const QuestionContainer = styled.div`
  background: #ffffff;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
  position: relative;
`

const QuestionTimer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${props => (props.isOvertime ? '#fef2f2' : '#f0fdf4')};
  color: ${props => (props.isOvertime ? '#dc2626' : '#16a34a')};
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 3px;
  border: 1px solid ${props => (props.isOvertime ? '#fecaca' : '#bbf7d0')};
`

const TimerIcon = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${props =>
    props.isActive ? 'currentColor' : 'rgba(0, 0, 0, 0.3)'};
`

const QuestionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`

const QuestionSide = styled.div`
  display: flex;
  flex-direction: column;
`

const OptionsSide = styled.div`
  display: flex;
  flex-direction: column;
`

const QuestionLabel = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 4px;
`

const OptionsHeader = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #16a34a;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 4px;
`

const QuestionTextContent = styled.div`
  font-size: 15px;
  line-height: 1.5;
  color: #374151;
  font-weight: 500;
  background: #f8fafc;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
`

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const OptionLabel = styled.label`
  display: flex;
  align-items: center; /* Ensures vertical centering */
  gap: 12px;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  background: #fff;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: #3b82f6;
    background: #f3f4f6;
  }

  ${props =>
    props.checked &&
    `
    border-color: #3b82f6;
    background: #eff6ff;
  `}
`

const RadioInput = styled.input`
  accent-color: #3b82f6;
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  vertical-align: middle; /* Ensures radio stays centered */
`

const OptionText = styled.div`
  display: flex;
  align-items: center; /* Ensures vertical centering */
  font-size: 13px;
  line-height: 1.4;
  /* Remove default margin from Markdown elements */
  & .markdown-body > * {
    margin: 0 !important;
  }
`

// Improved Sticky Navigation Container with smooth transitions
const StickyNavigationContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: ${props => {
    if (!props.sidebarExpanded) return '60px'
    return props.sidebarWidth ? `${props.sidebarWidth}px` : '25%'
  }};
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  padding: 12px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 50;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`

const BackButton = styled.button`
  background: #ffffff;
  color: #6b7280;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:disabled {
    background: #f3f4f6;
    color: #d1d5db;
    cursor: not-allowed;
  }
`

const NextButton = styled.button`
  background: ${props => (props.disabled ? '#d1d5db' : '#3b82f6')};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.disabled ? '#d1d5db' : '#2563eb')};
  }
`

const SkipButton = styled.button`
  background: #ffffff;
  color: #6b7280;
  border: 1px solid #9ca3af;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    color: #374151;
    border-color: #6b7280;
  }
`

const WarningMessage = styled.div`
  padding: 10px 14px;
  background: #fef2f2;
  color: #dc2626;
  border-radius: 4px;
  border: 1px solid #fecaca;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 16px;
`

const QuestionPopover = styled.div`
  position: fixed;
  left: ${props => (props.sidebarExpanded ? '25%' : '60px')};
  top: ${props => props.top}px;
  transform: translateY(-50%);
  background: #ffffff;
  color: #374151;
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  max-width: 350px;
  min-width: 280px;
  z-index: 2000;
  opacity: ${props => (props.show ? 1 : 0)};
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
  transition: all 0.2s ease;
  pointer-events: none;
  margin-left: 8px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: #ffffff;
  }

  &::after {
    content: '';
    position: absolute;
    left: -7px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: #e5e7eb;
    z-index: -1;
  }

  /* Enhanced popover content styling */
  .popover-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f3f4f6;
  }

  .popover-question-number {
    font-weight: 700;
    color: #3b82f6;
    font-size: 14px;
  }

  .popover-status {
    font-size: 10px;
    padding: 3px 8px;
    border-radius: 12px;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .popover-section {
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
    background: #f8fafc;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
  }

  .popover-text {
    line-height: 1.5;
    color: #374151;
    font-weight: 500;
  }

  .popover-time {
    font-size: 11px;
    color: #6b7280;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f3f4f6;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .popover-time-icon {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #10b981;
  }
`

const StatusSeparator = styled.div`
  width: 1.5px;
  height: 32px;
  background: linear-gradient(to bottom, #e5e7eb 60%, #d1d5db 100%);
  margin: 0 12px;
  border-radius: 2px;
  align-self: center;
`

// UPDATED: Enhanced Fullscreen Entry Modal Component to match shared UI
const FullscreenEntryModal = ({ onAccept }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Enable Full-Page Assessment Mode</FullscreenTitle>
        <FullscreenDescription>
          This assessment will open in full-page mode for optimal focus and
          proctoring purposes. The entire screen will be dedicated to the
          assessment.
        </FullscreenDescription>

        <FullscreenDescription>
          <div style={{ textAlign: 'left', width: '100%' }}>
            <span style={{ marginLeft: '27px' }}>
              <strong>Important:</strong> During this assessment:
            </span>
            <ul style={{ margin: '10px 0' }}>
              <li>The assessment will take over the entire screen</li>
              <li>Navigation elements will be hidden</li>
              <li>Switching tabs is not allowed (3 warnings; the fourth violation auto-submits)</li>
              <li>Browser navigation is disabled</li>
              <li>Key combinations like Alt+Tab are blocked</li>
            </ul>
            Violations may result in automatic submission of your assessment.
          </div>
        </FullscreenDescription>

        <PrimaryButton onClick={onAccept}>
          Accept & Start Assessment
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

// UPDATED: Enhanced Fullscreen Re-entry Modal Component to match shared UI
const FullscreenReentryModal = ({ onReturn, warningCount, lite = false }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Return to Assessment</FullscreenTitle>
        <FullscreenDescription>
          {lite
            ? 'You left the assessment. For security and proctoring purposes, please stay on this screen until you finish.'
            : 'You have exited the assessment mode. For security and proctoring purposes, you must return to full-page assessment mode to continue.'}
        </FullscreenDescription>

        <InfoContainer>
          <InfoIcon>!</InfoIcon>
          <InfoText>
            <strong>Warning {warningCount}/3:</strong> A fourth violation will result in automatic submission of your assessment
            and will be flagged as suspicious.
          </InfoText>
        </InfoContainer>

        <PrimaryButton onClick={onReturn}>
          {lite ? 'Resume Assessment' : 'Return to Assessments'}
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

// UPDATED: Violation Modal to match shared UI
const ViolationModal = ({ onSubmit }) => {
  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Assessment will be auto-submitted</FullscreenTitle>
        <FullscreenDescription>
          You&apos;ve violated the rules more than 3 times, so your assessment will be auto-submitted.
        </FullscreenDescription>

        <PrimaryButton onClick={onSubmit}>
          <span style={{ fontWeight: 'bold' }}>Understood</span>
        </PrimaryButton>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

// Submission Modal Component
const SubmissionModal = ({
  isOpen,
  onClose,
  onConfirm,
  answeredCount,
  unansweredCount,
  totalQuestions,
  isSubmitting,
}) => {
  if (!isOpen) return null

  return (
    <FullscreenModalOverlay>
      <FullscreenModalContent onClick={e => e.stopPropagation()}>
        <FullscreenTitle>Submit Assessment</FullscreenTitle>
        <FullscreenDescription>
          Are you sure you want to submit your assessment? This action cannot be
          undone.
        </FullscreenDescription>

        <div
          style={{
            background: '#f8fafc',
            padding: '16px',
            borderRadius: '6px',
            margin: '16px 0',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <div
            style={{ marginBottom: '8px', color: '#374151', fontWeight: '600' }}
          >
            Assessment Summary:
          </div>
          <div style={{ color: '#10b981', marginBottom: '4px' }}>
            Answered Questions: {answeredCount}
          </div>
          {unansweredCount > 0 && (
            <div style={{ color: '#9ca3af', marginBottom: '4px' }}>
              Unanswered Questions: {unansweredCount}
            </div>
          )}
          <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '8px' }}>
            Total Questions: {totalQuestions}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <SecondaryButton onClick={onClose} disabled={isSubmitting}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={onConfirm} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
          </PrimaryButton>
        </div>
      </FullscreenModalContent>
    </FullscreenModalOverlay>
  )
}

const Assessment = ({ setAssessment }) => {
  const dispatch = useDispatch()
  const questionsData = useSelector(selectors.getQuestions)
  const currentAssessment = useSelector(
    state => state.assessment.currentAssessment
  )
  const responses = useSelector(
    state => state.assessment.currentAssessment?.responses || {}
  )
  const timeSpentPerQuestion = useSelector(selectors.getTimeSpentPerQuestion)
  const activeQuestionTimer = useSelector(selectors.getActiveQuestionTimer)
  const practiceAccessState = useSelector(state => state.assessment?.practiceAccess)

  const studentData = useSelector(state => {
    return state.studentData?.studentDetails || state.auth?.user || null
  })

  const isMinusSystem = useMemo(() => {
    const rawData = questionsData?.data || questionsData || {}
    return !!rawData.isMinusSystem
  }, [questionsData])

  // FIXED: Get student name with fallbacks
  const getStudentName = () => {
    if (!studentData) return 'Unknown Student'

    return (
      studentData.full_name ||
      studentData.fullName ||
      studentData.firstName ||
      studentData.name ||
      (studentData.firstName && studentData.lastName
        ? `${studentData.firstName} ${studentData.lastName}`
        : '') ||
      'Unknown Student'
    )
  }

  // ADDED: Missing getTimeAllocation function
  const getTimeAllocation = () => {
    if (questions.length === 7) {
      return '10 minutes'
    } else if (questions.length <= 15) {
      return '20 minutes'
    } else if (questions.length === 25) {
      return '30 minutes'
    } else if (questions.length <= 30) {
      return '45 minutes'
    } else {
      return '60 minutes'
    }
  }

  // FIXED: Parse questions data more efficiently
  const parseQuestionsFromSections = useCallback(data => {
    if (!data || !data.questionsBySection) return []

    const questions = []
    const sections = ['critical reasoning', 'quantitative', 'logical reasoning']

    sections.forEach(sectionName => {
      if (
        data.questionsBySection[sectionName] &&
        Array.isArray(data.questionsBySection[sectionName])
      ) {
        data.questionsBySection[sectionName].forEach(question => {
          questions.push({
            ...question,
            section: sectionName,
            sectionDisplayName: sectionName
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' '),
          })
        })
      }
    })

    return questions
  }, [])

  // FIXED: Get questions data without accessing full Redux state
  const questions = useMemo(() => {
    const rawData = questionsData?.data || questionsData || {}
    return parseQuestionsFromSections(rawData)
  }, [questionsData, parseQuestionsFromSections])
  const [proctoringReady, setProctoringReady] = useState(false)
  // State for current question and UI
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [timeLeft, setTimeLeft] = useState(0)
  const [showWarning, setShowWarning] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [completedAssessmentData, setCompletedAssessmentData] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [timeUp, setTimeUp] = useState(false)
  const [finalSubmittedTime, setFinalSubmittedTime] = useState(null)
  const [sidebarExpanded, setSidebarExpanded] = useState(false)
  const [questionStatuses, setQuestionStatuses] = useState({})
  const [showSubmissionModal, setShowSubmissionModal] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(null) // Track sidebar width
  const [assessmentStartTime, setAssessmentStartTime] = useState(null)
  const [totalAssessmentTime, setTotalAssessmentTime] = useState(0)
  const [hoveredQuestion, setHoveredQuestion] = useState(null)
  const [popoverPosition, setPopoverPosition] = useState({ top: 0 })
  const hoverTimeoutRef = useRef(null)

  // Fullscreen state
  const proctoringEnabled = currentAssessment?.allowProctoring !== false
  // Fullscreen lockdown is only enforceable on devices that actually support
  // element fullscreen (desktop / macOS Safari / iPad). iPhone Safari cannot
  // fullscreen a web element, so there we run proctoring in "lite" mode:
  // snapshots + tab/app-switch detection stay ON, but the fullscreen entry gate,
  // re-entry-to-fullscreen and fullscreen-exit violations are skipped.
  const enforceFullscreen = proctoringEnabled && shouldEnforceFullscreen()
  const [showFullscreenModal, setShowFullscreenModal] = useState(enforceFullscreen)
  const [showReentryModal, setShowReentryModal] = useState(false)
  const [showViolationModal, setShowViolationModal] = useState(false)
  const [violationType, setViolationType] = useState('tab-switch')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isAssessmentFullscreen, setIsAssessmentFullscreen] = useState(false)
  const [tabSwitchCount, setTabSwitchCount] = useState(0)
  // Auto-activate when there is no fullscreen gate to pass (proctoring off, or lite mode)
  const [isAssessmentActive, setIsAssessmentActive] = useState(!enforceFullscreen)
  const [visitedQuestions, setVisitedQuestions] = useState(new Set())
  const assessmentActiveRef = useRef(isAssessmentActive)
  const completedRef = useRef(isCompleted)
  const handleSubmitRef = useRef(handleSubmit)
  const isAutoSubmittedRef = useRef(false)
  const violationSubmissionStartedRef = useRef(false)
  const violationSubmitTimerRef = useRef(null)
  const submitAfterViolation = () => {
    if (violationSubmissionStartedRef.current) return
    violationSubmissionStartedRef.current = true
    if (violationSubmitTimerRef.current) clearTimeout(violationSubmitTimerRef.current)
    isAutoSubmittedRef.current = true
    handleSubmitRef.current()
  }
  const sidebarRef = useRef(null)
  const questionTimerRef = useRef(null)
  const visibilityRef = useRef(document.visibilityState)

  const [captureCounter, setCaptureCounter] = useState(0)
  const captureRef = useRef(null)
  const canvasRef = useRef(null)
  const captureInterval = useRef(null)
  const proctoringActive = useRef(false)
  const capturingInProgress = useRef(false)

  // Report-only proctoring collector (aptitude-only, silent). Loads MediaPipe
  // FaceLandmarker lazily from CDN and batch-flushes behavioural events.
  const proctoringCollector = useProctoringCollector()
  const proctoringCollectorStarted = useRef(false)
  // Tier-2 adaptive snapshot burst: when the collector reports a suspicion
  // signal, we densify server snapshots for a short window to try to catch the
  // second device on camera. Epoch (ms) until which the burst cadence applies.
  const snapshotBurstUntilRef = useRef(0)

  // Camera guard: once started, if the camera turns off we silently try to
  // turn it back on in the background — no popup, no pause.
  const { cameraLost } = useCameraGuard({
    enabled: proctoringEnabled && !!assessmentStartTime && !isCompleted,
    getStream: () => (captureRef.current && captureRef.current.srcObject) || null,
  })

  const questionItemRefs = useRef([])

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isAssessmentActive && !isCompleted) {
        // Show browser confirmation dialog to prevent accidental exit
        e.preventDefault();
        e.returnValue = '';

        const timeElapsed = assessmentStartTime
          ? Math.floor((Date.now() - assessmentStartTime) / 1000)
          : totalAssessmentTime;

        trackAssessmentDroppedOff(
          timeElapsed,
          currentAssessment?.assessment_assigned_id,
          currentAssessment?.type,
          currentAssessment?.isCorporate,
          currentAssessment?.entityName
        );

        if (currentAssessment?.isPractice === "true" || currentAssessment?.isPractice === true) {
          trackPracticeSessionDroppedOff(currentAssessment?.assessment_assigned_id, currentAssessment?.type, null, timeElapsed, false, currentAssessment?.entityName);
        }

        if (currentAssessment.title === "Assessment #1" || currentAssessment.title === "Assessment #2") {
          console.log('CurrAssessment: ', JSON.stringify(currentAssessment, null, 2));
          const diagnosisNumber = 2 - currentAssessment.CommunicationDiagnosisRemain + 1;
          trackDiagnosisDroppedOff(diagnosisNumber, timeElapsed, currentAssessment?.assessment_assigned_id, currentAssessment?.type, currentAssessment?.entityName);
        }
      }
    };

    const handleUnload = () => {
      if (isAssessmentActive && !isCompleted) {
        const timeElapsed = assessmentStartTime
          ? Math.floor((Date.now() - assessmentStartTime) / 1000)
          : totalAssessmentTime;

        trackAssessmentDroppedOff(
          timeElapsed,
          currentAssessment?.assessment_assigned_id,
          currentAssessment?.type,
          currentAssessment?.isCorporate,
          currentAssessment?.entityName
        );

        if (currentAssessment?.isPractice !== undefined && (currentAssessment?.isPractice === "true" || currentAssessment?.isPractice === true)) {
          trackPracticeSessionDroppedOff(currentAssessment?.assessment_assigned_id, currentAssessment?.type, null, timeElapsed, false, currentAssessment?.entityName);
        }

        if (currentAssessment.title === "Assessment #1" || currentAssessment.title === "Assessment #2") {
          console.log('CurrAssessment: ', JSON.stringify(currentAssessment, null, 2));
          const diagnosisNumber = 2 - currentAssessment.CommunicationDiagnosisRemain + 1;
          trackDiagnosisDroppedOff(diagnosisNumber, timeElapsed, currentAssessment?.assessment_assigned_id, currentAssessment?.type, currentAssessment?.entityName);
        }
      }
    };

    // Prevent browser back button during active assessment
    const handlePopState = () => {
      if (isAssessmentActive && !isCompleted) {
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Push initial state to trap back button
    if (isAssessmentActive && !isCompleted) {
      window.history.pushState(null, '', window.location.href);
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAssessmentActive, isCompleted, assessmentStartTime, totalAssessmentTime, currentAssessment]);

  useEffect(() => {
    const handlePageHide = () => {
      if (isAssessmentActive && !isCompleted) {
        const timeElapsed = assessmentStartTime
          ? Math.floor((Date.now() - assessmentStartTime) / 1000)
          : totalAssessmentTime;

        trackAssessmentDroppedOff(
          timeElapsed,
          currentAssessment?.assessment_assigned_id,
          currentAssessment?.type,
          currentAssessment?.isCorporate,
          currentAssessment?.entityName
        );
      }
    };

    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isAssessmentActive, isCompleted, assessmentStartTime, totalAssessmentTime, currentAssessment]);



  // Add this useEffect to track question views
  useEffect(() => {
    if (questions[currentQuestionIndex]) {
      const currentQuestion = questions[currentQuestionIndex]
      const questionId = currentQuestion.id

      // Check if this is the first visit
      if (!visitedQuestions.has(questionId)) {
        // Mark as visited
        setVisitedQuestions(prev => new Set([...prev, questionId]))

      }

      // Scope the report-only proctoring fusion to the visible question so the
      // collector can compute a per-question "reading from another device"
      // verdict. No-op until the collector has started (gated to aptitude in
      // captureImage). Safe to call on every question change.
      if (proctoringCollectorStarted.current) {
        proctoringCollector.setActiveQuestion(currentQuestionIndex, questionId)
      }
    }
  }, [currentQuestionIndex, questions, visitedQuestions, currentAssessment])

  // Track sidebar width changes for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (sidebarRef.current && sidebarExpanded) {
        const width = sidebarRef.current.offsetWidth
        setSidebarWidth(width)
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)
    if (sidebarRef.current) {
      resizeObserver.observe(sidebarRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [sidebarExpanded])

  // Set timer based on number of questions
  useEffect(() => {
    let totalTime = 0 // in seconds

    if (questions.length <= 7) {

      totalTime = 10 * 60 // 10 minutes for exactly 7 questions
    } else if (questions.length <= 15) {
      totalTime = 20 * 60 // 20 minutes for 15 or fewer questions
    } else if (questions.length === 25) {
      totalTime = 30 * 60 // 30 minutes for the 25-question blueprint
    } else if (questions.length <= 30) {
      totalTime = 45 * 60 // 45 minutes for other 16-30 question sets
    } else {
      totalTime = 60 * 60 // 60 minutes for 31+ questions (40 questions)
    }

    setTimeLeft(totalTime)
  }, [questions.length])

  // Initialize assessment start time when assessment becomes active
  useEffect(() => {
    if (isAssessmentActive && !assessmentStartTime) {
      setAssessmentStartTime(Date.now())
    }
  }, [isAssessmentActive, assessmentStartTime])

  // Update total assessment time every second
  useEffect(() => {
    if (isAssessmentActive && assessmentStartTime) {
      const timer = setInterval(() => {
        const currentTime = Date.now()
        const totalTime = Math.floor((currentTime - assessmentStartTime) / 1000)
        setTotalAssessmentTime(totalTime)
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [isAssessmentActive, assessmentStartTime])

  // FIXED: Question timer effect with proper dependencies
  useEffect(() => {
    if (
      questions.length > 0 &&
      questions[currentQuestionIndex] &&
      isAssessmentActive
    ) {
      const currentQuestionId = questions[currentQuestionIndex].id

      // Only initialize if not already initialized
      if (timeSpentPerQuestion[currentQuestionId] === undefined) {
        dispatch(initQuestionTime(currentQuestionId))
      }

      // Clear any existing timer
      if (questionTimerRef.current) {
        clearInterval(questionTimerRef.current)
      }

      // Start the interval timer
      questionTimerRef.current = setInterval(() => {
        dispatch(tickQuestionTime(currentQuestionId))
      }, 1000)

      return () => {
        if (questionTimerRef.current) {
          clearInterval(questionTimerRef.current)
        }
      }
    }
  }, [dispatch, questions.length, currentQuestionIndex, isAssessmentActive]) // FIXED: Removed timeSpentPerQuestion from dependencies

  // FIXED: Update question statuses without causing infinite loops
  useEffect(() => {
    if (questions.length > 0) {
      const initialStatuses = {}
      questions.forEach((question, index) => {
        if (index === currentQuestionIndex) {
          initialStatuses[index] = 'current'
        } else if (responses[question.id] !== undefined) {
          initialStatuses[index] =
            responses[question.id] === 'SKIPPED' ? 'skipped' : 'attempted'
        } else {
          initialStatuses[index] = 'unattempted'
        }
      })

      // Only update if statuses have actually changed
      setQuestionStatuses(prevStatuses => {
        const hasChanged = Object.keys(initialStatuses).some(
          key => prevStatuses[key] !== initialStatuses[key]
        )
        return hasChanged ? initialStatuses : prevStatuses
      })
    }
  }, [questions.length, currentQuestionIndex, responses]) // FIXED: Removed questions from dependencies

  // Date-based timer: pin endTime once and compute remaining each tick.
  const endTimeRef = useRef(null)
  const isCompletedRef = useRef(isCompleted)
  const handleTimeUpRef = useRef(handleTimeUp)
  const submittedRef = useRef(false)
  useEffect(() => { isCompletedRef.current = isCompleted }, [isCompleted])
  useEffect(() => { handleTimeUpRef.current = handleTimeUp }, [handleTimeUp])

  useEffect(() => {
    if (timeLeft <= 0 || isCompleted) return
    if (endTimeRef.current === null) {
      endTimeRef.current = Date.now() + timeLeft * 1000
    }
  }, [timeLeft, isCompleted])

  useEffect(() => {
    const timer = setInterval(() => {
      if (!endTimeRef.current || isCompletedRef.current) return
      const remaining = Math.max(0, Math.ceil((endTimeRef.current - Date.now()) / 1000))
      if (remaining <= 0) {
        if (!submittedRef.current) {
          submittedRef.current = true
          setTimeLeft(0)
          handleTimeUpRef.current && handleTimeUpRef.current()
        }
        return
      }
      setTimeLeft(remaining)
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // Handle fullscreen change events (only when fullscreen lockdown is enforced)
  useEffect(() => {
    if (!enforceFullscreen) return

    const handleFullscreenChange = async () => {
      const isCurrentlyFullscreen = Boolean(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      setIsFullscreen(isCurrentlyFullscreen)
      setIsAssessmentFullscreen(isCurrentlyFullscreen)

      if (!isCurrentlyFullscreen && isMobileInterruptionFullscreenExit()) return

      // Always treat any exit from fullscreen as a violation
      if (isAssessmentActive && !isCurrentlyFullscreen && !isCompleted) {
        // Report-only proctoring: exiting fullscreen often precedes reading from
        // another app/window. Record it for the report (separate from the
        // existing in-exam warning/auto-submit flow below).
        if (proctoringCollectorStarted.current) {
          proctoringCollector.recordEvent('fullscreen_exit', { severity: 'medium', confidence: 1 })
        }
        setTabSwitchCount(prev => {
          const newCount = prev + 1
          if (newCount >= MAX_TAB_SWITCH_WARNINGS) {
            setShowViolationModal(true)
            setViolationType('fullscreen-exit')
            setIsAssessmentActive(false)
            trackAssessmentAutoSubmitted(
              currentAssessment.assessment_assigned_id,
              newCount,
              'Max violation count reached',
              currentAssessment.type,
              currentAssessment.isCorporate,
              currentAssessment.entityName
            )
            // Add a delay before auto-submitting
            isAutoSubmittedRef.current = true
            violationSubmitTimerRef.current = setTimeout(() => {
              submitAfterViolation()
            }, 5000)
          } else {
            setShowReentryModal(true)
            setIsAssessmentActive(false)
            trackFullscreenExited(currentAssessment.assessment_assigned_id, currentAssessment.type, newCount, currentAssessment.isCorporate, currentAssessment.entityName)
          }
          return newCount
        })
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    document.addEventListener('mozfullscreenchange', handleFullscreenChange)
    document.addEventListener('MSFullscreenChange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      )
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      )
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange)
    }
  }, [enforceFullscreen, isAssessmentActive, isCompleted, handleSubmit])

  const isUAT = (process.env.ENVIRONMENT || '').toUpperCase() === 'UAT';

  //detect right-click
  useEffect(() => {
    if (isUAT) return;
    const handleRightClick = (e) => {
      trackRightClick(currentAssessment.assessment_assigned_id, currentAssessment.type, currentAssessment.isCorporate, currentAssessment.entityName);
    }
    document.addEventListener('contextmenu', handleRightClick)
    return () => {
      document.removeEventListener('contextmenu', handleRightClick)
    }
  }, [])

  useEffect(() => {
    assessmentActiveRef.current = isAssessmentActive
    completedRef.current = isCompleted
    handleSubmitRef.current = handleSubmit
  }, [isAssessmentActive, isCompleted, handleSubmit])

  // Tab switch detection (only when proctoring is enabled)
  useEffect(() => {
    if (!proctoringEnabled) return

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'hidden' &&
        assessmentActiveRef.current &&
        !completedRef.current
      ) {
        const isMobileInterruption = markMobileInterruption()
        // Report-only proctoring: a hidden tab is the same-device cheat (another
        // tab/window: ChatGPT, Google) that gaze cannot see. Tee it into the
        // report pipeline; this does NOT change the existing in-exam warning flow.
        if (proctoringCollectorStarted.current) {
          proctoringCollector.recordEvent('tab_hidden', {
            severity: 'high',
            confidence: 1,
          })
        }
        if (isMobileInterruption) return
        setTabSwitchCount(prev => {
          const newCount = prev + 1
          if (newCount >= MAX_TAB_SWITCH_WARNINGS) {
            setShowViolationModal(true)
            setViolationType('tab-switch')
            setIsAssessmentActive(false)
            trackViolationDetected(currentAssessment.assessment_assigned_id, currentAssessment.type, newCount, 'tab-switch', currentAssessment.isCorporate, currentAssessment.entityName)
            trackAssessmentAutoSubmitted(
              currentAssessment.assessment_assigned_id,
              newCount,
              'Max violation count reached',
              currentAssessment.type,
              currentAssessment.isCorporate,
              currentAssessment.entityName
            )
            // Add a delay before auto-submitting
            isAutoSubmittedRef.current = true
            violationSubmitTimerRef.current = setTimeout(() => {
              submitAfterViolation()
            }, 5000)
          } else {
            setShowReentryModal(true)
            setIsAssessmentActive(false)
            trackViolationDetected(currentAssessment.assessment_assigned_id, currentAssessment.type, newCount, 'tab-switch', currentAssessment.isCorporate, currentAssessment.entityName)
          }
          return newCount
        })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [proctoringEnabled])

  // Fullscreen body class management (only when proctoring is enabled)
  useEffect(() => {
    if (!proctoringEnabled) return

    if (isAssessmentFullscreen) {
      document.body.classList.add('assessment-fullscreen-mode')
      // Hide navbar and other elements
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }
    } else {
      document.body.classList.remove('assessment-fullscreen-mode')
      // Show navbar and other elements
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
    }

    return () => {
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
    }
  }, [proctoringEnabled, isAssessmentFullscreen])

  useEffect(() => {
    if (
      questionItemRefs.current[currentQuestionIndex] &&
      typeof questionItemRefs.current[currentQuestionIndex].scrollIntoView ===
      'function'
    ) {
      questionItemRefs.current[currentQuestionIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [currentQuestionIndex, sidebarExpanded])

  // Snapshot cadence: 13s normally, 4s while a suspicion burst is active. Use a
  // self-rescheduling timeout (not a fixed setInterval) so the next delay can
  // adapt to the current burst window. captureInterval holds the timeout id.
  const SNAPSHOT_INTERVAL_MS = 13000
  const SNAPSHOT_BURST_INTERVAL_MS = 4000
  const setupProctoringInterval = () => {
    if (captureInterval.current) {
      clearTimeout(captureInterval.current)
    }
    const runOnce = async () => {
      if (proctoringActive.current && !capturingInProgress.current) {
        try {
          const imageBlob = await captureImage()
          if (imageBlob) {
            await uploadProctorImage(imageBlob)
          }
        } catch (error) {
          console.error('Error in proctoring interval:', error)
        }
      }
      const inBurst = Date.now() < snapshotBurstUntilRef.current
      captureInterval.current = setTimeout(runOnce, inBurst ? SNAPSHOT_BURST_INTERVAL_MS : SNAPSHOT_INTERVAL_MS)
    }
    captureInterval.current = setTimeout(runOnce, SNAPSHOT_INTERVAL_MS)
  }
  const isPracticeAssessment = useMemo(() => {
    return (
      currentAssessment?.isPractice ||
      currentAssessment?.type === 'practice' ||
      currentAssessment?.assessment_type === 'practice' ||
      questionsData?.isPractice ||
      questionsData?.data?.isPractice ||
      false
    )
  }, [currentAssessment, questionsData])

  const captureImage = async () => {
    if (capturingInProgress.current) return null
    capturingInProgress.current = true

    try {
      const safetyClearTimeout = setTimeout(() => {
        if (capturingInProgress.current) capturingInProgress.current = false
      }, 20000)

      if (!captureRef.current) {
        try {
          const proctorStream = await getUserMediaWithSelectedDevices({
            video: { width: 640, height: 480 },
            audio: false,
          })
          captureRef.current = document.createElement('video')
          mountHiddenProctorVideo(captureRef.current)
          watchProctorTrack(proctorStream, () => {
            unmountProctorVideo(captureRef.current)
            captureRef.current = null
            window.proctorStreamTracks = []
          })
          captureRef.current.srcObject = proctorStream
          captureRef.current.autoplay = true
          captureRef.current.muted = true
          // iOS Safari requires playsInline or hidden muted videos won't play
          // → MediaPipe gaze silently no-ops. Keep in sync with the
          // interview proctor video which already sets it.
          captureRef.current.playsInline = true
          await new Promise(resolve => {
            captureRef.current.onloadedmetadata = () => {
              captureRef.current.play().then(resolve).catch(resolve)
            }
            setTimeout(resolve, 1000)
          })
          // Stream + <video> are now live — kick the silent report-only collector
          // once, gated to aptitude. No-op + no CDN load for non-aptitude types.
          if (
            !proctoringCollectorStarted.current &&
            captureRef.current &&
            (currentAssessment?.type === 'Aptitude' ||
              currentAssessment?.assessment_type === 'Aptitude')
          ) {
            proctoringCollectorStarted.current = true
            proctoringCollector.start({
              videoEl: captureRef.current,
              assessmentAssignedId:
                currentAssessment?.assessment_assigned_id ||
                location?.state?.assessmentData?.assessment_assigned_id ||
                location?.state?.assessment_assigned_id,
              studentId:
                studentData?.id || studentData?.studentId || currentAssessment?.studentId,
              sessionId: null,
              // Aptitude is MCQ/typed-answer only — candidates normally look at the
              // keyboard or scratch paper, so gaze-away/head-turned proctoring is
              // false-positive noise here. Reading-from-a-device detection stays on.
              disableGazeEvents: true,
              // Tier-2: densify snapshots for 30s whenever a high-signal suspicion
              // fires, to try to catch the second device in a frame.
              onSuspicion: () => {
                snapshotBurstUntilRef.current = Date.now() + 30000
              },
            })
            // Second-display detection: an extended desktop during a remote test
            // is a strong tell (answers shown on the other monitor). Best-effort —
            // window.screen.isExtended needs the window-management permission and
            // is Chromium-only; absence is silently treated as "single display".
            // ponytail: one-shot at start; a monitor plugged in mid-exam isn't
            // re-checked. Upgrade path: subscribe to screen.addEventListener('change').
            try {
              if (window.screen && window.screen.isExtended === true) {
                proctoringCollector.recordEvent('second_display', { severity: 'high', confidence: 0.8 })
              }
            } catch (screenErr) {
              // permission denied / unsupported — leave it unflagged
            }
            // Scope the first question for the fusion tracker.
            if (questions[currentQuestionIndex]) {
              proctoringCollector.setActiveQuestion(currentQuestionIndex, questions[currentQuestionIndex].id)
            }
          }
          if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas')
            canvasRef.current.width = 640
            canvasRef.current.height = 480
          }
          if (!window.proctorStreamTracks) window.proctorStreamTracks = []
          proctorStream.getTracks().forEach(track => {
            window.proctorStreamTracks.push(track)
          })
          proctoringActive.current = true
        } catch (err) {
          console.error('Failed to create proctoring stream:', err)
          clearTimeout(safetyClearTimeout)
          capturingInProgress.current = false
          return null
        }
      }

      try {
        const context = canvasRef.current.getContext('2d')
        context.drawImage(
          captureRef.current,
          0,
          0,
          canvasRef.current.width,
          canvasRef.current.height
        )
        const blob = await new Promise(resolve => {
          canvasRef.current.toBlob(resolve, 'image/jpeg', 0.8)
        })
        clearTimeout(safetyClearTimeout)
        if (blob) {
          setCaptureCounter(prev => prev + 1)
          capturingInProgress.current = false
          return blob
        }
      } catch (err) {
        console.error('Error in standard capture mode:', err)
      }
      clearTimeout(safetyClearTimeout)
      capturingInProgress.current = false
      return null
    } catch (error) {
      console.error('Overall capture error:', error)
      capturingInProgress.current = false
      return null
    }
  }


  useEffect(() => {
    // FIXED: Only start proctoring when assessment becomes active
    if (!isAssessmentActive || isCompleted) {
      proctoringActive.current = false
      return
    }

    const assessmentAssignedId =
      currentAssessment?.assessment_assigned_id ||
      assessment?.assessment_assigned_id ||
      location?.state?.assessmentData?.assessment_assigned_id ||
      location?.state?.assessment_assigned_id

    const studentId =
      studentData?.id ||
      studentData?.studentId ||
      currentAssessment?.studentId

    // ponytail: studentId is optional for OTP-invite candidates (no portal login,
    // no redux studentData). The server resolves identity from assessmentAssignedId
    // -> primaryEmail, so only assessmentAssignedId is required to start proctoring.
    if (!assessmentAssignedId) {
      console.log('🔍 [PROCTORING] Missing assessmentAssignedId, skipping proctoring setup (will retry when assessment data loads)')
      proctoringActive.current = false
      return
    }

    console.log('🔍 [PROCTORING] Starting proctoring immediately when assessment starts with IDs:', {
      assessmentAssignedId,
      studentId: studentId || '(invite candidate — none)'
    })

    proctoringActive.current = true
    setupProctoringInterval()

      // Take initial capture immediately when assessment starts
      ; (async () => {
        try {
          console.log('🔍 [PROCTORING] Taking initial capture on assessment start')
          const imageBlob = await captureImage()
          if (imageBlob) {
            await uploadProctorImage(imageBlob)
            console.log('✅ [PROCTORING] Initial capture uploaded successfully')
          }
        } catch (error) {
          console.error('❌ [PROCTORING] Error in initial proctoring capture:', error)
        }
      })()

    return () => {
      proctoringActive.current = false
      if (captureInterval.current) {
        clearTimeout(captureInterval.current)
        captureInterval.current = null
      }
      if (captureRef.current && captureRef.current.srcObject) {
        captureRef.current.srcObject.getTracks().forEach(track => {
          if (track.readyState === 'live') track.stop()
        })
        captureRef.current.srcObject = null
      }
    }
  }, [
    isAssessmentActive, // Key dependency - start when assessment becomes active
    isCompleted,
    currentAssessment?.assessment_assigned_id,
    studentData?.id
  ])

  const uploadProctorImage = async imageBlob => {
    if (!imageBlob) return

    try {
      // FIXED: Get assessment_assigned_id with multiple fallbacks
      const assessmentAssignedId =
        currentAssessment?.assessment_assigned_id ||
        assessment?.assessment_assigned_id ||
        location?.state?.assessmentData?.assessment_assigned_id ||
        location?.state?.assessment_assigned_id

      // FIXED: Get student ID with multiple fallbacks  
      const studentId =
        studentData?.id ||
        studentData?.studentId ||
        currentAssessment?.studentId

      if (!assessmentAssignedId) {
        console.error('❌ [PROCTORING] No assessment_assigned_id found for proctoring upload')
        return
      }

      // ponytail: studentId optional — OTP-invite candidates have none; server
      // resolves identity from assessmentAssignedId. Send empty string when absent
      // so the multipart field is well-formed but non-blocking.
      console.log('🔍 [PROCTORING] Uploading with IDs:', {
        assessmentAssignedId,
        studentId: studentId || '(invite candidate — none)'
      })

      const base64Image = await blobToBase64(imageBlob)
      const formData = new FormData()
      const byteCharacters = atob(base64Image)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const imageFile = new Blob([byteArray], { type: 'image/jpeg' })

      formData.append('assessmentAssignedId', assessmentAssignedId)
      formData.append('studentId', studentId || '')
      formData.append('image', imageFile)

      await studentRequest.post(
        'students/assessments/proctoring/uploadImage',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )

      console.log('✅ [PROCTORING] Image uploaded successfully')
    } catch (error) {
      console.error('❌ [PROCTORING] Error processing proctoring image:', error)
      // Don't throw error to avoid breaking the assessment flow
    }
  }

  const blobToBase64 = blob => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1]
        resolve(base64String)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }




  useEffect(() => {
    return () => {
      if (captureInterval.current) {
        clearTimeout(captureInterval.current)
        captureInterval.current = null
      }
      if (window.proctorStreamTracks) {
        window.proctorStreamTracks.forEach(track => {
          if (track.readyState === 'live') track.stop()
        })
        window.proctorStreamTracks = []
      }
      if (captureRef.current) {
        captureRef.current.srcObject = null
        captureRef.current = null
      }
      // Best-effort flush of any buffered proctoring events if the candidate
      // navigates away without submitting. Fire-and-forget — unmount can't await.
      if (proctoringCollectorStarted.current) {
        proctoringCollector.flush().catch(() => {})
      }
    }
  }, [])

  // Silently re-acquire the proctor camera in the background — no popup, no pause.
  const reacquiringCameraRef = useRef(false)
  const reacquireCameraSilently = useCallback(async () => {
    if (reacquiringCameraRef.current) return
    reacquiringCameraRef.current = true
    try {
      try {
        if (captureRef.current && captureRef.current.srcObject) {
          captureRef.current.srcObject.getTracks().forEach(t => { try { t.stop() } catch (_) {} })
        }
      } catch (e) { /* noop */ }
      captureRef.current = null
      window.proctorStreamTracks = []
      const blob = await captureImage()
      if (blob) await uploadProctorImage(blob)
    } catch (e) {
      console.warn('[camera-guard] silent re-acquire failed:', e?.message || e)
    } finally {
      reacquiringCameraRef.current = false
    }
  }, [])

  // While the camera is lost mid-assessment, keep retrying silently in the
  // background until it's back — the assessment keeps running uninterrupted.
  useEffect(() => {
    if (!cameraLost) return undefined
    reacquireCameraSilently()
    const id = setInterval(reacquireCameraSilently, 5000)
    return () => clearInterval(id)
  }, [cameraLost, reacquireCameraSilently])

  // UPDATED: Enhanced fullscreen request function
  const requestFullscreenAccess = async () => {
    // Best-effort camera start: try to get a live camera before starting, but
    // don't block the assessment on it — the guard keeps retrying in the background.
    if (proctoringEnabled && !isCameraLive(captureRef.current && captureRef.current.srcObject)) {
      try { await captureImage() } catch (e) { /* fall through */ }
    }
    try {
      const element = document.documentElement

      // Add body class to hide navbar BEFORE fullscreen request
      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }

      // Wait a bit to ensure fullscreen is active
      setTimeout(() => {
        const isCurrentlyFullscreen = Boolean(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        )

        setIsAssessmentFullscreen(true) // Set assessment fullscreen regardless of browser fullscreen
        setShowFullscreenModal(false)
        setIsAssessmentActive(true)
        setProctoringReady(true) // <-- Add this
      }, 500)

      if (currentAssessment.title === "Assessment #1" || currentAssessment.title === "Assessment #2") {
        const diagnosisNumber = 2 - currentAssessment.diagnosisRemain + 1;
        trackDiagnosisStarted(diagnosisNumber, currentAssessment.assessment_assigned_id, currentAssessment.type, currentAssessment.entityName);
      }
      trackAssessmentStarted(currentAssessment.assessment_assigned_id, currentAssessment.type, currentAssessment.isCorporate, currentAssessment.entityName);
      trackFullscreenEntered(currentAssessment.assessment_assigned_id, currentAssessment.type, false, currentAssessment.isCorporate, currentAssessment.entityName);
    } catch (error) {
      console.error('Error entering fullscreen:', error)
      // Still proceed with assessment even if fullscreen fails
      setIsAssessmentFullscreen(true)
      setShowFullscreenModal(false)
      setIsAssessmentActive(true)
    }
  }

  // UPDATED: Enhanced return to fullscreen handler
  const handleReturnToFullscreen = async () => {
    setShowReentryModal(false)

    try {
      const element = document.documentElement

      // Add body class to hide navbar
      document.body.classList.add('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = 'none'
      }

      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      }

      trackFullscreenEntered(currentAssessment.assessment_assigned_id, currentAssessment.type, true, currentAssessment.isCorporate, currentAssessment.entityName);
      setIsAssessmentFullscreen(true)
      setIsAssessmentActive(true)
      setProctoringReady(true) // <-- Add this
    } catch (error) {
      console.error('Error re-entering fullscreen:', error)
      setIsAssessmentFullscreen(true) // Continue with fullscreen UI
      setIsAssessmentActive(true)
    }
  }

  // Lite-mode (iPhone) re-entry: there is no fullscreen to return to, so just
  // dismiss the warning and resume. The tab-switch/app-switch was still counted.
  const handleReturnLite = () => {
    setShowReentryModal(false)
    setIsAssessmentActive(true)
    setProctoringReady(true)
  }

  // UPDATED: Add fullscreen exit function
  const exitFullscreen = async () => {
    try {
      // Remove body class to show navbar
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }

      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      }

      setIsAssessmentFullscreen(false)
    } catch (error) {
      console.error('Error exiting fullscreen:', error)
      // Still clean up UI state
      document.body.classList.remove('assessment-fullscreen-mode')
      const navbar = document.querySelector(
        'nav, .navbar, .header, .app-header, .top-nav'
      )
      if (navbar) {
        navbar.style.display = ''
      }
      setIsAssessmentFullscreen(false)
    }
  }

  const stopProctoringCamera = () => {
    proctoringActive.current = false
    if (captureInterval.current) {
      clearTimeout(captureInterval.current)
      captureInterval.current = null
    }
    if (captureRef.current && captureRef.current.srcObject) {
      try {
        captureRef.current.srcObject.getTracks().forEach(track => track.stop())
      } catch (e) { /* noop */ }
      captureRef.current.srcObject = null
    }
    captureRef.current = null
    if (window.proctorStreamTracks) {
      window.proctorStreamTracks.forEach(track => {
        try { track.stop() } catch (e) { /* noop */ }
      })
      window.proctorStreamTracks = []
    }
  }

  // FIXED: Memoize handleSubmit to prevent recreation on every render
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return

    const totalQuestions = questions.length
    const answeredQuestions = Object.keys(responses).length

    try {
      setIsSubmitting(true)

      // Stop camera/proctoring stream on submit
      stopProctoringCamera()

      // Flush + stop the report-only collector so its events are ingested BEFORE
      // the server finalizes the proctoring report on submit.
      if (proctoringCollectorStarted.current) {
        try {
          await proctoringCollector.stop()
        } catch (e) {
          console.warn('[PROCTORING_COLLECTOR] stop/flush failed (non-fatal):', e?.message || e)
        }
        proctoringCollectorStarted.current = false
      }

      // Pause current question timer before submit
      if (questions[currentQuestionIndex]) {
        dispatch(pauseQuestionTime(questions[currentQuestionIndex].id))
      }

      // Calculate total taken time in seconds
      const finalTotalTime = assessmentStartTime
        ? Math.floor((Date.now() - assessmentStartTime) / 1000)
        : totalAssessmentTime

      // Get student name properly
      const fullName = getStudentName()

      // Prepare submission payload
      const submissionPayload = {
        response: responses,
        assessment_assigned_id: currentAssessment?.assessment_assigned_id,
        full_name: fullName,
        totalTakenTime: finalTotalTime,
        isAutoSubmitted: isAutoSubmittedRef.current,
      }

      console.log('Submitting assessment with payload:', submissionPayload)

      // Prepare assessment data for completion
      const assessmentDataForCompletion = {
        title: currentAssessment?.title || 'Aptitude Assessment',
        type: currentAssessment?.type || 'Aptitude',
        totalQuestions: totalQuestions,
        answeredQuestions: answeredQuestions,
        responses: responses,
        timeSpent: timeSpentPerQuestion,
        totalTakenTime: finalTotalTime,
      }

      // Submit assessment
      const submitResult = await dispatch(submitAssessment(submissionPayload))

      if (submitResult) {
        if (currentAssessment.title === "Assessment #1" || currentAssessment.title === "Assessment #2") {
          console.log('currentAssessment', JSON.stringify(currentAssessment, null, 2))
          const diagnosisNumber = 2 - currentAssessment.AptitudeDiagnosisRemain + 1;
          trackDiagnosisCompleted(diagnosisNumber, null, finalTotalTime, currentAssessment.assessment_assigned_id, currentAssessment.type, currentAssessment.entityName);
          trackDiagnosisProgressUpdated(
            currentAssessment.assessment_assigned_id,
            currentAssessment.type,
            diagnosisNumber, 2 - diagnosisNumber, currentAssessment.entityName)

          if (diagnosisNumber >= 2) {
            trackPracticeUnlocked(currentAssessment.type)
          }
        }
        console.log('Current Assessment: ', JSON.stringify(currentAssessment, null, 2));
        if (currentAssessment.isPractice === "true" || currentAssessment.isPractice === true) {
          trackPracticeSessionCompleted(currentAssessment.assessment_assigned_id, currentAssessment.type, finalTotalTime);
        }
        trackAssessmentCompleted(finalTotalTime, currentAssessment.assessment_assigned_id, currentAssessment.type, currentAssessment.isCorporate, currentAssessment.entityName)
        // UPDATED: Exit fullscreen and clean up before showing completion
        await exitFullscreen()

        // Success - prepare for completion
        setFinalSubmittedTime(timeLeft)
        setCompletedAssessmentData(assessmentDataForCompletion)

        // Set completion state
        setIsCompleted(true)
        setIsAssessmentActive(false)
        setShowSubmissionModal(false)
      } else {
        throw new Error('Submission failed - no result returned')
      }
    } catch (error) {
      console.error('Submit assessment error:', error)
      alert('Failed to submit assessment. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }, [
    isSubmitting,
    questions,
    responses,
    currentQuestionIndex,
    assessmentStartTime,
    totalAssessmentTime,
    studentData,
    currentAssessment,
    timeSpentPerQuestion,
    timeLeft,
    dispatch,
    getStudentName,
  ])

  // FIXED: Memoize handleTimeUp to prevent recreation
  const handleTimeUp = useCallback(async () => {
    if (timeUp || isSubmitting) return // Prevent multiple calls

    try {
      setTimeUp(true)
      trackAssessmentAutoSubmitted(
        currentAssessment.assessment_assigned_id,
        null,
        'time-up',
        currentAssessment.type,
        currentAssessment.isCorporate,
        currentAssessment.entityName
      )
      await handleSubmit()
    } catch (error) {
      console.error('Auto-submit error:', error)
      alert('Time is up! Assessment auto-submitted.')
      stopProctoringCamera()
      setIsCompleted(true)
    }
  }, [timeUp, isSubmitting, handleSubmit])

  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`
  }

  const formatQuestionTime = seconds => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = seconds % 60

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`
  }

  const handleNext = () => {
    const currentQuestion = questions[currentQuestionIndex]
    // Pause current question timer
    dispatch(pauseQuestionTime(currentQuestion.id))

    // Check if an option is selected for the current question
    if (!responses[currentQuestion.id]) {
      setShowWarning(true)
      return
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setShowWarning(false)
    } else {
      // On last question, show submission modal instead of browser alert
      setShowSubmissionModal(true)
    }
  }

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      // Pause current question timer
      const currentQuestion = questions[currentQuestionIndex]
      dispatch(pauseQuestionTime(currentQuestion.id))

      setCurrentQuestionIndex(currentQuestionIndex - 1)
      setShowWarning(false)
    }
  }

  const handleOptionSelect = (questionId, optionText, sectionDisplayName) => {

    dispatch(
      saveQuestionResponse(
        questionId,
        optionText,
        currentAssessment.assessment_assigned_id
      )
    )

    // Update question status
    setQuestionStatuses(prev => ({
      ...prev,
      [currentQuestionIndex]: 'attempted',
    }))

    trackQuestionAnswered(
      currentAssessment.assessment_assigned_id,
      currentAssessment.type,
      questionId,
      sectionDisplayName,
      optionText,
      null,
      currentAssessment.isCorporate,
      currentAssessment.entityName
    )

    // Report-only proctoring: record the answer so the collector can correlate a
    // read-glance immediately followed by an answer (read-then-transcribe).
    if (proctoringCollectorStarted.current) {
      proctoringCollector.recordAnswer(questionId)
    }

    setShowWarning(false)
  }

  const handleSkip = () => {
    const currentQuestion = questions[currentQuestionIndex]
    trackQuestionSkipped(
      currentAssessment.assessment_assigned_id,
      currentAssessment.type,
      currentQuestion.id, currentQuestion.sectionDisplayName, currentAssessment.isCorporate, currentAssessment.entityName);
    // Pause current question timer
    dispatch(pauseQuestionTime(currentQuestion.id))

    // Mark as skipped in responses with "SKIPPED" for API compatibility
    dispatch(
      saveQuestionResponse(
        currentQuestion.id,
        'SKIPPED',
        currentAssessment.assessment_assigned_id
      )
    )

    // Update question status
    setQuestionStatuses(prev => ({
      ...prev,
      [currentQuestionIndex]: 'skipped',
    }))

    // Move to next question
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setShowWarning(false)
    } else {
      // On last question, show submission modal instead of browser alert
      setShowSubmissionModal(true)
    }
  }

  const handleQuestionSelect = index => {
    // Pause current question timer
    if (questions[currentQuestionIndex]) {
      dispatch(pauseQuestionTime(questions[currentQuestionIndex].id))
    }
    setCurrentQuestionIndex(index)
  }

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded)
    if (!sidebarExpanded) {
      setSidebarWidth(null) // Reset width when expanding
    }
  }

  const handleSubmissionConfirm = () => {
    handleSubmit()
  }

  const handleSubmissionCancel = () => {
    setShowSubmissionModal(false)
  }

  const handleQuestionHover = (index, event) => {
    if (sidebarExpanded) return // Only show popover when collapsed

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    const rect = event.currentTarget.getBoundingClientRect()
    setPopoverPosition({ top: rect.top + rect.height / 2 })

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredQuestion(index)
    }, 200) // Shorter delay for better UX
  }

  const handleQuestionLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }

    setTimeout(() => {
      setHoveredQuestion(null)
    }, 100)
  }

  if (questions.length === 0) {
    return <div>Loading questions...</div>
  }

  // UPDATED: Fix stats calculation to ensure proper categorization
  const attemptedCount = Object.values(responses).filter(
    r => r && r !== 'SKIPPED'
  ).length
  const skippedCount = Object.values(responses).filter(
    r => r === 'SKIPPED'
  ).length
  const remainingCount = questions.length - attemptedCount - skippedCount
  const unansweredCount = remainingCount + skippedCount // Questions with no response at all

  // Get current question and its timer info
  const currentQuestion = questions[currentQuestionIndex]
  const currentQuestionTime = timeSpentPerQuestion[currentQuestion?.id] || 0
  const isTimerActive = activeQuestionTimer === currentQuestion?.id
  const isOvertime = currentQuestionTime > 120 // 2 minutes threshold
  const isOptionSelected =
    currentQuestion &&
    responses[currentQuestion.id] &&
    responses[currentQuestion.id] !== 'SKIPPED'

  // Calculate progress - count all responses (answered + skipped)
  const progressPercentage = Math.round(
    (Object.keys(responses).length / questions.length) * 100
  )

  // Determine if sidebar is wider than 25% (show full text)
  const showFullText = sidebarWidth && sidebarWidth > window.innerWidth * 0.25

  // Show completion component if assessment is completed
  if (isCompleted) {
    return (
      <Completion
        assessmentData={completedAssessmentData}
        submittedTime={finalSubmittedTime}
        setAssessment={setAssessment}
      />
    )
  }

  if (enforceFullscreen && showFullscreenModal) {
    return <FullscreenEntryModal onAccept={requestFullscreenAccess} />
  }

  if (proctoringEnabled && showReentryModal) {
    return (
      <FullscreenReentryModal
        onReturn={enforceFullscreen ? handleReturnToFullscreen : handleReturnLite}
        warningCount={tabSwitchCount}
        lite={!enforceFullscreen}
      />
    )
  }

  if (proctoringEnabled && showViolationModal) {
    return <ViolationModal onSubmit={submitAfterViolation} />
  }

  if (proctoringEnabled && !isAssessmentActive) {
    return null
  }

  return (
    <FullscreenAssessmentContainer isFullscreen={isAssessmentFullscreen} className="exam-shell">
      <SidebarNav
        ref={sidebarRef}
        isExpanded={sidebarExpanded}
        sidebarWidth={sidebarWidth}
      >
        <SidebarHeader isExpanded={sidebarExpanded}>
          {sidebarExpanded && (
            <SidebarTitle isExpanded={sidebarExpanded}>
              Questions ({questions.length})
            </SidebarTitle>
          )}
          <ToggleButton onClick={toggleSidebar} isExpanded={sidebarExpanded}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d={sidebarExpanded ? 'M18 6L6 18M6 6l12 12' : 'M9 18l6-6-6-6'}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ToggleButton>
        </SidebarHeader>

        <SidebarContent isExpanded={sidebarExpanded}>
          {/* {sidebarExpanded && <SectionTitle isExpanded={sidebarExpanded}>Aptitude Assessment</SectionTitle>} */}

          <QuestionList>
            {questions.map((question, index) => {
              let status = 'unattempted'
              if (index === currentQuestionIndex) {
                status = 'current'
              } else if (responses[question.id] !== undefined) {
                status =
                  responses[question.id] === 'SKIPPED' ? 'skipped' : 'attempted'
              }

              // Show section header for first question of each section
              const showSectionHeader =
                index === 0 ||
                (index > 0 && questions[index - 1].section !== question.section)

              return (
                <React.Fragment key={index}>
                  {showSectionHeader && sidebarExpanded && (
                    <div
                      style={{
                        fontSize: '10px',
                        fontWeight: '700',
                        color: '#3b82f6',
                        margin: '12px 6px 6px 6px',
                        padding: '4px 0',
                        borderBottom: '1px solid #e5e7eb',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {question.sectionDisplayName}
                    </div>
                  )}
                  <QuestionItem
                    ref={el => (questionItemRefs.current[index] = el)}
                    isExpanded={sidebarExpanded}
                    onClick={() => handleQuestionSelect(index)}
                    onMouseEnter={e => handleQuestionHover(index, e)}
                    onMouseLeave={handleQuestionLeave}
                  >
                    <QuestionNumber
                      status={status}
                      isExpanded={sidebarExpanded}
                    >
                      {index + 1}
                    </QuestionNumber>
                    {sidebarExpanded && (
                      <>
                        <QuestionText isExpanded={sidebarExpanded}>
                          <div className="markdown-body">
                            <ReactMarkdown
                              remarkPlugins={[remarkMath]}
                              rehypePlugins={[rehypeKatex]}
                              skipHtml={true}
                            >
                              {question.text}
                            </ReactMarkdown>
                          </div>
                        </QuestionText>
                      </>
                    )}
                  </QuestionItem>
                </React.Fragment>
              )
            })}
          </QuestionList>
        </SidebarContent>
      </SidebarNav>

      {/* Enhanced Hover Popover */}
      {hoveredQuestion !== null && !sidebarExpanded && (
        <QuestionPopover
          show={true}
          sidebarExpanded={sidebarExpanded}
          top={popoverPosition.top}
        >
          <div className="popover-header">
            <span className="popover-question-number">
              Question {hoveredQuestion + 1}
            </span>
            <span
              className="popover-status"
              style={{
                background: (() => {
                  const question = questions[hoveredQuestion]
                  let status = 'unattempted'
                  if (hoveredQuestion === currentQuestionIndex) {
                    status = 'current'
                  } else if (responses[question?.id] !== undefined) {
                    status =
                      responses[question.id] === 'SKIPPED'
                        ? 'skipped'
                        : 'attempted'
                  }

                  switch (status) {
                    case 'current':
                      return '#3b82f6'
                    case 'attempted':
                      return '#10b981'
                    case 'skipped':
                      return '#6b7280'
                    default:
                      return '#9ca3af'
                  }
                })(),
              }}
            >
              {(() => {
                const question = questions[hoveredQuestion]
                let status = 'unattempted'
                if (hoveredQuestion === currentQuestionIndex) {
                  status = 'current'
                } else if (responses[question?.id] !== undefined) {
                  status =
                    responses[question.id] === 'SKIPPED'
                      ? 'skipped'
                      : 'attempted'
                }

                switch (status) {
                  case 'current':
                    return 'Current'
                  case 'attempted':
                    return 'Answered'
                  case 'skipped':
                    return 'Skipped'
                  default:
                    return 'Pending'
                }
              })()}
            </span>
          </div>

          {questions[hoveredQuestion]?.sectionDisplayName && (
            <div className="popover-section">
              {questions[hoveredQuestion].sectionDisplayName}
            </div>
          )}

          <div className="popover-text">
            <div className="markdown-body">
              <ReactMarkdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
                skipHtml={true}
              >
                {questions[hoveredQuestion]?.text}
              </ReactMarkdown>
            </div>
          </div>

          {questions[hoveredQuestion]?.id !== undefined && (
            <div className="popover-time">
              <div className="popover-time-icon"></div>
              Time spent:{' '}
              {formatQuestionTime(
                timeSpentPerQuestion[questions[hoveredQuestion].id] || 0
              )}
            </div>
          )}
        </QuestionPopover>
      )}

      <ContentArea className="exam-scroll">
        <AssessmentContent className="exam-scroll">
          <AssessmentHeader>
            <AssessmentInfo>
              <LogoContainer>
                <PluginLiveLogo />
              </LogoContainer>
              <VerticalSeparator />
              <AssessmentTitle>Aptitude Assessment</AssessmentTitle>
            </AssessmentInfo>
            <AssessmentTimer>{formatTime(timeLeft)}</AssessmentTimer>
          </AssessmentHeader>

          <AssessmentSubtitle>
            <SubtitleText>
              Question {currentQuestionIndex + 1} •{' '}
              {currentQuestion?.sectionDisplayName || 'Assessment'}
            </SubtitleText>

            {isMinusSystem && (
              <NegativeMarkingInfo>
                <InfoIconSvg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </InfoIconSvg>
                Negative marking is enabled
              </NegativeMarkingInfo>
            )}
          </AssessmentSubtitle>

          {/* Status section moved above question */}
          <StatusSection>
            <ProgressContainer>
              <ProgressText>
                Progress: {Object.keys(responses).length} of {questions.length}{' '}
                ({progressPercentage}%)
              </ProgressText>
              <ProgressBar progress={progressPercentage} />
            </ProgressContainer>

            <StatusItem>
              <StatusNumber color="#10b981">{attemptedCount}</StatusNumber>
              <StatusLabel>Answered</StatusLabel>
            </StatusItem>

            <StatusItem>
              <StatusNumber color="#6b7280">{unansweredCount}</StatusNumber>
              <StatusLabel>Unanswered</StatusLabel>
            </StatusItem>

            <StatusSeparator />

            <StatusItem>
              <StatusNumber color="#9ca3af">{skippedCount}</StatusNumber>
              <StatusLabel> Skipped</StatusLabel>
            </StatusItem>
          </StatusSection>

          {showWarning && (
            <WarningMessage>
              Please select an option before proceeding.
            </WarningMessage>
          )}

          <QuestionContainer>
            {/* <QuestionTimer isOvertime={isOvertime}>
              <TimerIcon isActive={isTimerActive} />
              {formatQuestionTime(currentQuestionTime)}
            </QuestionTimer> */}

            <QuestionLayout>
              <QuestionSide>
                <QuestionLabel>Question</QuestionLabel>
                <QuestionTextContent>
                  <div className="markdown-body">
                    <ReactMarkdown
                      remarkPlugins={[remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                      skipHtml={true}
                    >
                      {currentQuestion.text}
                    </ReactMarkdown>
                  </div>
                </QuestionTextContent>
              </QuestionSide>

              <OptionsSide>
                <OptionsHeader>Select one answer</OptionsHeader>
                <OptionsContainer>
                  {currentQuestion.options.map(option => (
                    <OptionLabel
                      key={option.id}
                      checked={responses[currentQuestion.id] === option.text}
                    >
                      <RadioInput
                        type="radio"
                        name={`question_${currentQuestion.id}`}
                        checked={responses[currentQuestion.id] === option.text}
                        onClick={() => {
                          if (responses[currentQuestion.id] === option.text) {
                            // Uncheck if already selected, send "SKIPPED"
                            dispatch(
                              saveQuestionResponse(
                                currentQuestion.id,
                                "SKIPPED", // <-- send "SKIPPED" for uncheck
                                currentAssessment.assessment_assigned_id
                              )
                            )

                          } else {
                            handleOptionSelect(currentQuestion.id, option.text, currentQuestion.sectionDisplayName)
                          }
                        }}
                        readOnly
                      />
                      <OptionText>
                        <div className="markdown-body">
                          <ReactMarkdown
                            remarkPlugins={[remarkMath]}
                            rehypePlugins={[rehypeKatex]}
                            skipHtml={true}
                          >
                            {option.text}
                          </ReactMarkdown>
                        </div>
                      </OptionText>
                    </OptionLabel>
                  ))}
                </OptionsContainer>
              </OptionsSide>
            </QuestionLayout>
          </QuestionContainer>
        </AssessmentContent>

        {/* Improved Sticky Navigation at bottom */}
        <StickyNavigationContainer
          sidebarExpanded={sidebarExpanded}
          sidebarWidth={sidebarWidth}
          className="exam-actions"
        >
          <BackButton
            onClick={handleBack}
            disabled={currentQuestionIndex === 0}
          >
            Back
          </BackButton>

          <ButtonContainer>
            <SkipButton onClick={handleSkip}>Skip</SkipButton>
            {currentQuestionIndex === questions.length - 1 ? (
              <NextButton
                onClick={() => setShowSubmissionModal(true)}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </NextButton>
            ) : (
              <NextButton onClick={handleNext} disabled={!isOptionSelected}>
                Next
              </NextButton>
            )}
          </ButtonContainer>
        </StickyNavigationContainer>
      </ContentArea>

      {/* UPDATED: Submission Modal with fixed props */}
      <SubmissionModal
        isOpen={showSubmissionModal}
        onClose={handleSubmissionCancel}
        onConfirm={handleSubmissionConfirm}
        answeredCount={attemptedCount}
        unansweredCount={unansweredCount}
        totalQuestions={questions.length}
        isSubmitting={isSubmitting}
      />
    </FullscreenAssessmentContainer>
  )
}

// AssessmentModule wrapper component
const AssessmentModule = ({
  assessmentType,
  assessmentData,
  setAssessment,
}) => {
  switch (assessmentType) {
    case 'instructions':
      return <div>Instructions component not implemented yet</div>
    case 'assessment':
      return (
        <>
          <MarkdownGlobalStyle />
          <Assessment setAssessment={setAssessment} />
        </>
      )
    case 'completion':
      return (
        <Completion
          assessmentData={assessmentData}
          setAssessment={setAssessment}
        />
      )
    default:
      return <Assessment setAssessment={setAssessment} />
  }
}

export default AssessmentModule
